if (!customElements.get('hp-menu-v2')) {
  customElements.define('hp-menu-v2', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.drawer = this.querySelector('dialog');
      this.dropdowns = [...this.querySelectorAll('[data-dropdown]')];
      this.hoverTimers = new Map();
      this.stickyHeader = this.querySelector('sticky-header');
      this.syncStickyOverlay = () => {
        if (!this.stickyHeader) return;
        this.stickyHeader.preventHide = this.drawer.open || !!this.querySelector('details-modal details[open]');
      };
      // Reuse Sense's scroll controller; connect its disclosure-close hook to this markup.
      customElements.whenDefined('sticky-header').then(() => {
        if (signal.aborted || !this.stickyHeader) return;
        this.stickyHeader.closeMenuDisclosure = () => this.closeDropdowns();
      });
      this.update = () => {
        this.toggleAttribute('data-solid', this.dataset.overlay !== 'true' || window.scrollY > 2);
        document.documentElement.style.setProperty('--header-height', `${this.querySelector('header').offsetHeight}px`);
      };
      window.addEventListener('scroll', this.update, { passive: true, signal });
      window.addEventListener('pageshow', this.update, { signal });
      this.resize = new ResizeObserver(this.update);
      this.resize.observe(this.querySelector('header'));
      this.update();
      this.announcementState = new Map();
      this.syncAnnouncements = () => {
        document.querySelectorAll('.shopify-section-group-header-group slideshow-component').forEach(slideshow => {
          const wrapper = slideshow.closest('.shopify-section-group-header-group');
          if (getComputedStyle(wrapper).display !== 'none' || !slideshow.slider || !slideshow.pause) return;
          if (!this.announcementState.has(slideshow)) this.announcementState.set(slideshow, slideshow.slider.dataset.autoplay);
          slideshow.slider.dataset.autoplay = 'false';
          slideshow.pause();
        });
      };
      customElements.whenDefined('slideshow-component').then(() => { if (!signal.aborted) this.syncAnnouncements(); });
      document.addEventListener('shopify:section:load', this.syncAnnouncements, { signal });
      customElements.whenDefined('cart-drawer').then(() => {
        if (signal.aborted) return;
        const trigger = this.querySelector('#cart-icon-bubble');
        const cart = document.querySelector('cart-drawer');
        if (trigger?.getAttribute('role') !== 'button') cart?.setHeaderCartIconAccessibility?.();
      });

      this.querySelectorAll('[data-open-drawer]').forEach(button => {
        button.addEventListener('click', () => this.openDrawer(button), { signal });
      });
      this.querySelector('[data-close-drawer]').addEventListener('click', () => this.drawer.close(), { signal });
      this.drawer.addEventListener('click', event => {
        if (event.target === this.drawer) {
          const r = this.drawer.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right) this.drawer.close();
        }
      }, { signal });
      this.drawer.addEventListener('close', () => this.afterClose(), { signal });
      this.drawer.addEventListener('click', event => {
        if (event.target.closest('a[href]')) this.drawer.close();
      }, { signal });
      this.dropdowns.forEach(dropdown => {
        const summary = dropdown.querySelector('summary');
        let openedByHover = false;
        summary.setAttribute('aria-expanded', String(dropdown.open));
        dropdown.addEventListener('toggle', () => summary.setAttribute('aria-expanded', String(dropdown.open)), { signal });
        dropdown.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.hoverTimers.get(dropdown));
          this.closeDropdowns(dropdown);
          openedByHover = !dropdown.open;
          dropdown.open = true;
        }, { signal });
        dropdown.addEventListener('pointerleave', event => {
          if (event.pointerType !== 'mouse') return;
          this.hoverTimers.set(dropdown, setTimeout(() => {
            if (!dropdown.contains(document.activeElement)) dropdown.open = false;
          }, 180));
        }, { signal });
        summary.addEventListener('click', event => {
          event.preventDefault();
          this.closeDropdowns(dropdown);
          dropdown.open = openedByHover || !dropdown.open;
          openedByHover = false;
        }, { signal });
        dropdown.addEventListener('focusout', () => {
          queueMicrotask(() => { if (!dropdown.contains(document.activeElement)) dropdown.open = false; });
        }, { signal });
      });
      document.addEventListener('pointerdown', event => {
        if (!event.target.closest('[data-dropdown]')) this.closeDropdowns();
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || this.drawer.open) return;
        const opened = this.dropdowns.find(item => item.open);
        if (opened) {
          event.preventDefault();
          this.closeDropdowns();
          opened.querySelector('summary').focus();
        }
      }, { signal });
      this.breakpoint = matchMedia('(min-width: 1100px)');
      this.breakpoint.addEventListener('change', () => this.closeDropdowns(), { signal });
      document.addEventListener('shopify:section:select', event => {
        if (event.detail.sectionId !== this.dataset.sectionId) return;
        this.stickyHeader?.reveal();
        this.update();
      }, { signal });
      this.addEventListener('focusin', () => {
        this.syncStickyOverlay();
        this.stickyHeader?.reveal();
      }, { signal });
      this.querySelector('details-modal details')?.addEventListener('toggle', this.syncStickyOverlay, { signal });
    }

    closeDropdowns(except) {
      this.dropdowns.forEach(item => { if (item !== except) item.open = false; });
    }

    openDrawer(trigger) {
      if (this.stickyHeader) {
        this.stickyHeader.preventHide = true;
        this.stickyHeader.reveal();
      }
      this.returnFocus = trigger;
      this.closeDropdowns();
      this.querySelectorAll('[data-open-drawer]').forEach(button => button.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('hp-menu-v2-lock');
      this.drawer.showModal();
    }

    afterClose() {
      this.syncStickyOverlay();
      document.body.classList.remove('hp-menu-v2-lock');
      this.querySelectorAll('[data-open-drawer]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      // A link inside a closed dropdown is not a valid restoration target.
      const trigger = this.returnFocus?.getClientRects().length ? this.returnFocus : this.querySelector('.hp-menu-v2__actions [data-open-drawer]');
      trigger?.focus({ preventScroll: true });
      this.update();
    }

    disconnectedCallback() {
      if (this.drawer?.open) this.drawer.close();
      document.body.classList.remove('hp-menu-v2-lock');
      this.abort?.abort();
      this.resize?.disconnect();
      this.hoverTimers?.forEach(timer => clearTimeout(timer));
      this.announcementState?.forEach((autoplay, slideshow) => {
        slideshow.slider.dataset.autoplay = autoplay;
        if (autoplay === 'true' && slideshow.isConnected && slideshow.getClientRects().length) slideshow.setAutoPlay();
      });
    }
  });
}
